import { Button } from "antd";
import type { ReactNode } from "react";
import { useInRouterContext, useNavigate } from "react-router";

import { APP_VERSION_TYPE } from "@zhihaoscm/meta";
import { Permission } from "@/views/components/Permission";

export interface MyTaskStatAction {
    key: string;
    label: string;
    state?: string;
    count: number;
    /** Vue v-auth 的权限集合；多个权限按任一命中处理。 */
    permissions?: readonly string[];
}

export interface MyTaskNavigationTarget {
    path: string;
    query?: Record<string, string | number | Array<string | number>>;
}

/** Vue 待办统计卡片点击后使用的列表入口和筛选条件。 */
export const MY_TASK_STAT_NAVIGATION: Record<string, MyTaskNavigationTarget> = {
    consignment: { path: "/productManagement/consignment", query: { consignPublishState: 1 } },
    groupPurchase: { path: "/productManagement/groupPurchase", query: { groupPublishState: 1 } },
    bargain: { path: "/productManagement/bargain", query: { bargainPublishState: 1 } },
    collection: { path: "/tradeManage/collectionManage", query: { receiveState: 1 } },
    sample: { path: "/userOperation/sampleApply", query: { state: 1 } },
    refund: { path: "/tradeManage/refundManage", query: { states: 1 } },
    // Vue SHIP_SERVICE_FEE_STATE.UN_CONFIRM 的状态码为 2。
    shipInfoServiceFee: { path: "/expenseManagement/serviceCostBill", query: { state: 2 } },
    oilFee: { path: "/expenseManagement/serviceCostBill", query: { state: 1 } },
    quote: { path: "/logistics/shippingNeed/platformShipNeed", query: { orderAcceptanceState: 2 } },
    deposit: { path: "/logistics/shippingNeed/waybillManagement", query: { state: [10] } },
    departure: { path: "/logistics/shippingNeed/waybillManagement", query: { state: [11] } },
    transporting: { path: "/logistics/shippingNeed/waybillManagement", query: { state: [12] } },
    unload: { path: "/logistics/shippingNeed/waybillManagement", query: { state: [13] } },
    unfinished: { path: "/logistics/shippingNeed/waybillManagement", query: { state: [15] } },
    oilBooking: { path: "/logistics/oil/oilTypeManagement", query: { state: 1 } },
    oilPlan: { path: "/logistics/oil/oilTypeManagement", query: { state: 2 } },
    oilConfirm: { path: "/logistics/oil/oilTypeManagement", query: { state: 2 } },
    oilComplete: { path: "/logistics/oil/oilTypeManagement", query: { state: 2 } },
    offline: { path: "/logistics/device/deviceManagement", query: { online: 0 } },
    claiming: { path: "/logistics/deviceClaim", query: { state: 1 } },
    clue: { path: "/logistics/cluesManage/shippingOwnerClues", query: { state: [1] } },
    audit: { path: "/logistics/shipManage/shipCertification", query: { state: [1] } },
    deviceWarning: { path: "/logistics/monitor/monitorWarn", query: { handleState: 1, type: 1 } },
    shipWarning: { path: "/logistics/monitor/monitorWarn", query: { handleState: 1, type: 2 } },
    unlabeled: { path: "/logistics/monitor/modelData", query: { state: 1 } },
    institution: { path: "/lianYunUser/institutionApply", query: { state: 1 } },
    message: { path: "/userOperation/messageFeedback", query: { state: 1 } },
    features: { path: "/logistics/featuresFeedback", query: { state: [1] } },
    help: { path: "/userOperation/lianYunAbout/helpCenter", query: { state: 2 } },
    corpus: { path: "/intelligentAssistant/aiCorpus/library", query: { state: 1 } },
    funding: { path: "/userOperation/fundingClues", query: { states: 1 } },
    productPending: { path: "/dashboard/index", query: { state: 1 } },
    productUnreleased: { path: "/dashboard/index", query: { state: 2 } },
    composite: { path: "/dashboard/index", query: { state: 1 } },
    freightPending: { path: "/dashboard/index", query: { state: 1 } },
    freightUnreleased: { path: "/dashboard/index", query: { state: 2 } },
    freightComposite: { path: "/dashboard/index", query: { state: 1 } },
    information: { path: "/dashboard/index", query: { state: 1 } },
    academy: { path: "/dashboard/index", query: { state: 1 } },
    oilPending: { path: "/dashboard/index", query: { state: 1 } },
    oilUnreleased: { path: "/dashboard/index", query: { state: 2 } }
};

/** 把 Vue 的对象查询参数编码成 React Router 可直接导航的地址。 */
export const buildMyTaskNavigationPath = ({ path, query }: MyTaskNavigationTarget) => {
    if (!query) return path;
    const searchParams = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach(item => searchParams.append(key, String(item)));
        } else {
            searchParams.set(key, String(value));
        }
    });
    const search = searchParams.toString();
    return search ? `${path}?${search}` : path;
};

/** 保留 Vue 待办跳转使用的筛选缓存，供目标列表回显历史条件。 */
export const persistMyTaskNavigationQuery = (target?: MyTaskNavigationTarget) => {
    if (target?.query) localStorage.setItem("MytaskGotoListPageQuery", JSON.stringify(target.query));
};

/** 可点击的待办统计项；数量为零时沿用 Vue 的“不触发跳转”规则。 */
export function MyTaskAction({ stat, children }: { stat: MyTaskStatAction; children: ReactNode }) {
    const target = MY_TASK_STAT_NAVIGATION[stat.key];
    // 统计组件也会被独立渲染做单元测试，未挂载 Router 时保持纯展示能力。
    const hasRouter = useInRouterContext();
    const content =
        !target || !hasRouter ? (
            <>{children}</>
        ) : (
            <NavigableMyTaskAction stat={stat} target={target}>
                {children}
            </NavigableMyTaskAction>
        );
    if (stat.permissions?.length) {
        return <Permission anyOf={[...stat.permissions]}>{content}</Permission>;
    }
    return content;
}

function NavigableMyTaskAction({
    stat,
    target,
    children
}: {
    stat: MyTaskStatAction;
    target: MyTaskNavigationTarget;
    children: ReactNode;
}) {
    const navigate = useNavigate();
    const label = stat.state ? `${stat.label}：${stat.state}` : stat.label;
    const handleClick = () => {
        if (!stat.count) return;
        persistMyTaskNavigationQuery(target);
        void navigate(buildMyTaskNavigationPath(target));
    };
    return (
        <Button
            aria-label={label}
            className="h-auto w-full p-0 text-inherit"
            disabled={!stat.count}
            type="link"
            onClick={handleClick}
        >
            {children}
        </Button>
    );
}

interface MyTaskNewsRoute {
    listPath: string;
    detailPath: string;
    idInPath?: boolean;
    /** 旧 Vue 仅配置列表路由的模块，非删除日志也不拼接业务编号。 */
    singlePath?: boolean;
    /** 旧 Vue 仅配置详情路由的模块，删除日志也必须携带业务编号进入详情。 */
    deleteUsesDetail?: boolean;
}

/** 当前 React 已迁移页面可安全承接的 Vue 操作日志高亮路由。 */
const MY_TASK_NEWS_ROUTES: Record<number, MyTaskNewsRoute> = {
    // 旧 Vue 单路由模块仅需进入当前 React 列表页，不拼接业务编号。
    5: {
        listPath: "/lianYunUser/memberLevel",
        detailPath: "/lianYunUser/memberLevel",
        singlePath: true
    },
    // 旧 Vue 模块 6 只有企业定制详情路由，删除日志也保留编号进入详情。
    6: {
        listPath: "/lianYunUser/enterpriseCustom",
        detailPath: "/lianYunUser/enterpriseCustom/info",
        idInPath: true,
        deleteUsesDetail: true
    },
    // 旧 Vue 砂石/运价指数均复用 React 指数管理列表和版本详情入口。
    7: {
        listPath: "/indexResearch",
        detailPath: "/indexResearch/versionInfo",
        idInPath: true
    },
    10: {
        listPath: "/indexResearch",
        detailPath: "/indexResearch/versionInfo",
        idInPath: true
    },
    // 旧 Vue 模块 17 只有意向需求详情路由，删除日志也保留编号进入详情。
    17: {
        listPath: "/purchasingManage/intendedDemand",
        detailPath: "/purchasingManage/intendedDemand/detail",
        idInPath: true,
        deleteUsesDetail: true
    },
    18: {
        listPath: "/purchasingManage/project",
        detailPath: "/purchasingManage/project/info",
        idInPath: true
    },
    19: {
        listPath: "/purchasingManage/contract",
        detailPath: "/purchasingManage/contract/info",
        idInPath: true
    },
    // 旧 Vue 模块 20 的码头详情通过查询参数接收业务编号。
    20: {
        listPath: "/logistics/portWharfBerth/wharfManagement",
        detailPath: "/logistics/portWharfBerth/wharfDetail"
    },
    21: {
        listPath: "/logistics/device/deviceManagement",
        detailPath: "/logistics/device/deviceManagement/info",
        idInPath: true
    },
    // 旧 Vue 模块 22、25 只有详情路由，删除日志同样进入详情。
    22: {
        listPath: "/logistics/deviceClaim",
        detailPath: "/logistics/deviceClaim/info",
        idInPath: true,
        deleteUsesDetail: true
    },
    24: {
        listPath: "/logistics/shipManage/shipInfo",
        detailPath: "/logistics/shipManage/shipInfo/shipInfoDetail",
        idInPath: true
    },
    25: {
        listPath: "/logistics/shipManage/shipCertification",
        detailPath: "/logistics/shipManage/shipCertification/info",
        idInPath: true,
        deleteUsesDetail: true
    },
    // 旧 Vue 模块 30、37、38、40、41 均只有列表路由。
    30: {
        listPath: "/logistics/monitor/modelData",
        detailPath: "/logistics/monitor/modelData",
        singlePath: true
    },
    33: {
        listPath: "/expenseManagement/willPay",
        detailPath: "/expenseManagement/userAccount/willPay/detail",
        idInPath: true,
        deleteUsesDetail: true
    },
    34: {
        listPath: "/expenseManagement/serviceCostBill",
        detailPath: "/expenseManagement/serviceCostBill/detail",
        idInPath: true,
        deleteUsesDetail: true
    },
    37: {
        listPath: "/manageCenter/systemManage/organizationalStructure",
        detailPath: "/manageCenter/systemManage/organizationalStructure",
        singlePath: true
    },
    38: {
        listPath: "/manageCenter/systemManage/backendAccount",
        detailPath: "/manageCenter/systemManage/backendAccount",
        singlePath: true
    },
    39: {
        listPath: "/manageCenter/systemManage/role",
        detailPath: "/manageCenter/systemManage/role/detail",
        idInPath: true
    },
    40: {
        listPath: "/userOperation/adBanner/banner",
        detailPath: "/userOperation/adBanner/banner",
        singlePath: true
    },
    41: {
        listPath: "/userOperation/adBanner/ad",
        detailPath: "/userOperation/adBanner/ad",
        singlePath: true
    },
    16: { listPath: "/productManagement/consignment", detailPath: "/productManagement/consignment/detail" },
    23: {
        listPath: "/logistics/cluesManage/shippingOwnerClues",
        detailPath: "/logistics/cluesManage/shippingOwnerCluesDetail",
        idInPath: true
    },
    27: {
        listPath: "/logistics/shippingNeed/platformShipNeed",
        detailPath: "/logistics/shippingNeed/platformShipNeedInfo",
        idInPath: true
    },
    28: {
        listPath: "/logistics/shippingNeed/waybillManagement",
        detailPath: "/logistics/shippingNeed/waybillManagementInfo",
        idInPath: true
    },
    29: { listPath: "/logistics/monitor/monitorWarn", detailPath: "/logistics/monitor/monitorWarn", idInPath: true },
    31: {
        listPath: "/logistics/monitor/identifyRecord",
        detailPath: "/logistics/monitor/identifyRecord",
        idInPath: true
    },
    42: { listPath: "/userOperation/adPageManage", detailPath: "/userOperation/adPageManage/detail" },
    62: {
        listPath: "/logistics/featuresFeedback",
        detailPath: "/logistics/featuresFeedback/info",
        idInPath: true
    },
    // 旧 Vue 模块 68 只有回调记录列表，非删除日志通过查询参数保留业务编号。
    68: {
        listPath: "/manageCenter/callbackRecord",
        detailPath: "/manageCenter/callbackRecord"
    },
    // 旧 Vue 模块 70 的删除日志回到油站列表，其他日志进入油站详情。
    70: {
        listPath: "/logistics/oil/oilSite",
        detailPath: "/logistics/oil/OilSiteDetail",
        idInPath: true
    },
    // 旧 Vue 模块 73 只有 OilTypeManagementDetail 路由，删除日志不回列表。
    73: {
        listPath: "/logistics/oil/oilTypeManagement",
        detailPath: "/logistics/oil/oilTypeManagement/detail",
        idInPath: true,
        deleteUsesDetail: true
    },
    // 旧 Vue 模块 69 只有 PopUpAdvertDetail 路由，删除日志也保留详情编号。
    69: {
        listPath: "/userOperation/popUpAdvert",
        detailPath: "/userOperation/popUpAdvert/detail",
        deleteUsesDetail: true
    },
    // 旧 Vue 商品日志模块仅配置详情路由，删除日志同样保留商品编号。
    79: {
        listPath: "/productManagement/consignment",
        detailPath: "/productManagement/consignment/detail",
        deleteUsesDetail: true
    },
    80: {
        listPath: "/productManagement/groupPurchase",
        detailPath: "/productManagement/groupPurchase/detail",
        deleteUsesDetail: true
    },
    81: {
        listPath: "/productManagement/bargain",
        detailPath: "/productManagement/bargain/detail",
        deleteUsesDetail: true
    },
    // 旧 Vue 交易及用户运营日志模块均配置列表、详情两条路由。
    82: {
        listPath: "/tradeManage/invoiceManage",
        detailPath: "/tradeManage/invoiceDetail"
    },
    83: {
        listPath: "/tradeManage/enterpriseManage",
        detailPath: "/tradeManage/enterpriseManage/detail"
    },
    84: {
        listPath: "/userOperation/fundingClues",
        detailPath: "/userOperation/fundingClues/detail"
    },
    85: {
        listPath: "/tradeManage/tradeOrder",
        detailPath: "/tradeManage/tradeOrder/detail"
    },
    86: {
        listPath: "/userOperation/sampleApply",
        detailPath: "/userOperation/sampleApply/detail"
    },
    87: {
        listPath: "/tradeManage/refundManage",
        detailPath: "/tradeManage/refund/detail"
    },
    88: {
        listPath: "/productActivity/activityList",
        detailPath: "/productActivity/activity/detail"
    },
    89: {
        listPath: "/tradeManage/collectionManage",
        detailPath: "/tradeManage/collection/detail"
    },
    // 旧 Vue 模块 50–54 均配置列表、详情两条路由，删除日志回到对应列表。
    50: {
        listPath: "/purchasingManage/payment",
        detailPath: "/purchasingManage/payment/info",
        idInPath: true
    },
    51: {
        listPath: "/purchasingManage/delivery",
        detailPath: "/purchasingManage/delivery/detail",
        idInPath: true
    },
    52: {
        listPath: "/purchasingManage/receiptManagement",
        detailPath: "/purchasingManage/receiptManagement/detail",
        idInPath: true
    },
    53: {
        listPath: "/purchasingManage/purchaseReconciliation",
        detailPath: "/purchasingManage/reconciliation/info",
        idInPath: true
    },
    54: {
        listPath: "/expenseManagement/serviceCostBill",
        detailPath: "/expenseManagement/serviceCostBill/detail",
        idInPath: true
    },
    // 旧 Vue 模块 55、56 的记录详情统一复用 React 指数管理版本详情入口。
    55: {
        listPath: "/indexResearch",
        detailPath: "/indexResearch/versionInfo",
        idInPath: true
    },
    56: {
        listPath: "/indexResearch",
        detailPath: "/indexResearch/versionInfo",
        idInPath: true
    },
    // 旧 Vue 模块 64 使用应用列表和应用详情两条路由，详情编号位于路径中。
    64: {
        listPath: "/manageCenter/application",
        detailPath: "/manageCenter/applicationDetail",
        idInPath: true
    },
    // 旧 Vue 模块 61 使用帮助中心列表和问题详情两条路由。
    61: {
        listPath: "/userOperation/lianYunAbout/helpCenter",
        detailPath: "/userOperation/lianYunAbout/helpCenter/info"
    },
    // 旧 Vue 模块 60、63 只有列表路由，所有日志类型都回到列表。
    60: {
        listPath: "/userOperation/searchFound",
        detailPath: "/userOperation/searchFound",
        singlePath: true
    },
    63: {
        listPath: "/intelligentAssistant/aiHotWord",
        detailPath: "/intelligentAssistant/aiHotWord",
        singlePath: true
    },
    65: {
        listPath: "/intelligentAssistant/aiCorpus/publish",
        detailPath: "/intelligentAssistant/aiCorpus/publish",
        singlePath: true
    },
    66: {
        listPath: "/intelligentAssistant/aiCorpus/library",
        detailPath: "/intelligentAssistant/aiCorpus/library",
        singlePath: true
    },
    58: {
        listPath: "/lianYunUser/institutionApply",
        detailPath: "/lianYunUser/institutionApply/info",
        idInPath: true
    },
    59: {
        listPath: "/userOperation/messageFeedback",
        detailPath: "/userOperation/messageFeedback/details"
    },
    43: {
        listPath: "/manageCenter/tagManage/tagList",
        detailPath: "/manageCenter/tagManage/tagList",
        singlePath: true
    },
    44: {
        listPath: "/manageCenter/businessSetting",
        detailPath: "/manageCenter/businessSetting",
        singlePath: true
    },
    45: {
        listPath: "/userOperation/lianYunAbout/aboutUs",
        detailPath: "/userOperation/lianYunAbout/aboutUs",
        singlePath: true
    },
    46: {
        listPath: "/userOperation/lianYunAbout/userAgreement",
        detailPath: "/userOperation/lianYunAbout/userAgreement",
        singlePath: true
    },
    47: {
        listPath: "/userOperation/lianYunAbout/privacyAgreement",
        detailPath: "/userOperation/lianYunAbout/privacyAgreement",
        singlePath: true
    },
    48: {
        listPath: "/userOperation/lianYunAbout/memberAgreement",
        detailPath: "/userOperation/lianYunAbout/memberAgreement",
        singlePath: true
    },
    // 旧 Vue 协议模块 49、72、76 均只有文章路由，沿用协议页默认应用上下文。
    49: {
        listPath: "/userOperation/lianYunAbout/deviceClaimAgreement",
        detailPath: "/userOperation/lianYunAbout/deviceClaimAgreement",
        singlePath: true
    },
    72: {
        listPath: "/userOperation/lianYunAbout/oilingAgreement",
        detailPath: "/userOperation/lianYunAbout/oilingAgreement",
        singlePath: true
    },
    76: {
        listPath: "/userOperation/lianYunAbout/shippingContractServiceAgreement",
        detailPath: "/userOperation/lianYunAbout/shippingContractServiceAgreement",
        singlePath: true
    },
    67: {
        listPath: "/userOperation/appVersion",
        detailPath: "/userOperation/appVersion",
        singlePath: true
    }
};

export interface MyTaskNewsOperation {
    bizNo?: string;
    content?: string;
    highLight?: string;
    operationModule?: number;
    operationType?: number;
}

const ARTICLE_CONTEXT_MODULES = new Set([45, 46, 47, 48, 72, 76]);
const ARTICLE_APP_TYPES: Record<string, number> = {
    链云砂石小程序: 1,
    链云船务小程序: 2,
    链云砂石PC: 3,
    链云砂石APP: 4,
    链云船务APP: 5,
    链云船务PC: 6
};

/** 将旧 Vue 操作日志高亮终端转换为文章查询使用的 APP_TYPE 编号。 */
export function getArticleAppTypeFromHighlight(highLight?: string): number | undefined {
    return highLight ? ARTICLE_APP_TYPES[highLight] : undefined;
}

/** 为关于链云文章导航保留旧 Vue 的终端查询参数。 */
function appendArticleContext(path: string, operation: MyTaskNewsOperation): string {
    if (!ARTICLE_CONTEXT_MODULES.has(operation.operationModule || 0)) return path;
    const appType = getArticleAppTypeFromHighlight(operation.highLight);
    return appType ? `${path}?typeId=${appType}` : path;
}

/** 按旧 Vue APP 版本日志文案识别链云船务或志豪链云版本类型。 */
export function getAppVersionTypeFromOperationContent(content?: string): number {
    return content?.includes(APP_VERSION_TYPE.SHIP.intro) ? APP_VERSION_TYPE.SHIP.code : APP_VERSION_TYPE.LIAN_YUN.code;
}

/** 进入 APP 版本列表前写入旧 Vue 使用的版本类型上下文。 */
export function prepareMyTaskNewsNavigation(operation: MyTaskNewsOperation): void {
    if (operation.operationModule !== 67) return;
    localStorage.setItem("appVersion", String(getAppVersionTypeFromOperationContent(operation.content)));
}

/** 生成高亮动态的详情/列表地址；删除日志沿用 Vue 跳转列表规则。 */
export const getMyTaskNewsNavigation = (operation: MyTaskNewsOperation) => {
    const route = operation.operationModule === undefined ? undefined : MY_TASK_NEWS_ROUTES[operation.operationModule];
    if (!route || !operation.bizNo) return undefined;
    if (route.singlePath) return { path: appendArticleContext(route.listPath, operation) };
    if (operation.operationType === 3 && !route.deleteUsesDetail) {
        return { path: appendArticleContext(route.listPath, operation) };
    }
    const id = encodeURIComponent(operation.bizNo);
    return {
        path: appendArticleContext(
            route.idInPath ? `${route.detailPath}/${id}` : `${route.detailPath}?id=${id}`,
            operation
        )
    };
};
